/**
 * `useChangeEventStream` — subscribe to the backend's SSE change-event stream and invalidate
 * TanStack Query families when events arrive (friction-log fix #92, 2026-05-29).
 *
 * When an MCP client or another tab writes to the backend (`create_note`, `update_notebook`,
 * `empty_trash`, etc.) the backend publishes a coarse `{"families": [...]}` event to a
 * per-workspace SSE stream. This hook keeps that stream open for the life of the app and
 * translates each event into `queryClient.invalidateQueries` calls so the affected views
 * refetch on their next render pass.
 *
 * Wired in once at the app root (via the editor surface or `<App>`); call sites don't need to
 * think about it. TanStack collapses concurrent refetches and the family-prefix invalidation
 * is cheap.
 *
 * # Connection lifecycle
 * - Opens on mount. Uses `fetch` with the `Authorization: Bearer <token>` header (matching
 *   the rest of the client) and a ReadableStream consumer. EventSource is *not* used because
 *   it cannot set custom headers — and we want to share the existing auth model.
 * - Reconnects with exponential backoff on close / network blip. Capped at 30s between
 *   attempts so a long backend outage doesn't burn cycles, and reset on a successful event.
 * - Cleans up on unmount via an AbortController.
 *
 * # Family → query-key mapping
 * The backend emits coarse family strings; we translate them into the matching key prefixes
 * here. The mapping is duplicated server-side (see the docstring in
 * `aletheia/api/core/events.py`) and the two sides MUST stay in sync — if either side adds a
 * new resource, both must learn about it.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/** Server-emitted event shape. ``families`` is a list of resource roots; ``ts`` is a server
 *  clock for debugging. */
interface ChangeEvent {
  families: string[];
  ts: number;
}

/**
 * Resolve the backend base URL the same way `api/client.ts` does — Tauri-injected globals
 * first, then Vite env, then empty (same-origin proxy / dev proxy fallback).
 *
 * Duplicated rather than exported from client.ts because the lookup is one line, this hook is
 * the only other caller, and the dependency direction stays clean (hooks/ doesn't import from
 * the same internals client.ts uses for its own bookkeeping).
 */
function getBaseUrl(): string {
  if (typeof window === 'undefined') return '';
  const globals = (window as unknown as { __ALETHEIA__?: { apiBaseUrl?: string } }).__ALETHEIA__;
  if (globals?.apiBaseUrl) return globals.apiBaseUrl;
  const env = import.meta.env as Record<string, string | undefined> | undefined;
  return env?.VITE_ALETHEIA_BACKEND_URL ?? '';
}

/** Same loopback-token resolution `api/client.ts` uses. */
function getToken(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  const globals = (window as unknown as { __ALETHEIA__?: { loopbackToken?: string } }).__ALETHEIA__;
  if (globals?.loopbackToken) return globals.loopbackToken;
  const env = import.meta.env as Record<string, string | undefined> | undefined;
  return env?.VITE_ALETHEIA_API_TOKEN;
}

/**
 * Map a server-emitted family string to the TanStack Query key prefixes to invalidate.
 * Prefix matching means `['notebooks']` invalidates every key starting with `'notebooks'` —
 * children, notes, ancestors, etc.
 */
export const FAMILY_TO_KEYS: Record<string, ReadonlyArray<ReadonlyArray<string>>> = {
  // A note changed (created / updated / soft-deleted / restored / hard-deleted): refetch
  // every note read AND the notebook's note-listing AND the wikilink autocomplete list.
  notes: [['note'], ['note-chunks'], ['notebooks', 'notes'], ['note-titles']],
  // A notebook itself changed (created / renamed / moved / soft-deleted): refetch the tree
  // plus the flat `list_notebooks` (notebook write tools consume the flat list for title
  // resolution; if it's stale, "create note with notebook=X by title" fails).
  notebooks: [['notebooks'], ['list_notebooks']],
  // Trash content changed (something hit the trash, restored from it, or was emptied):
  // refetch the soft-deleted listings — both notes (#90) and notebooks (#95).
  trash: [
    ['health', 'soft-deleted'],
    ['health', 'soft-deleted-notebooks'],
  ],
  // Action item changed (manual create / update / status toggle / delete): refetch the
  // action-items list and the parent note (whose companion includes the action items).
  action_items: [['action-items'], ['note']],
  // Tag applied / removed: refetch tag aggregates + the affected note (companion holds
  // user_tags / auto_tags).
  tags: [['tags'], ['related-tags'], ['tag-timeline'], ['note']],
  // 2026-06-01 — user-authored UI themes (Settings → Themes tab). A theme
  // created/edited/deleted on one device refetches the list everywhere; the
  // active-theme application picks up the latest values on the next render.
  themes: [['custom-themes']],
};

/**
 * Open the SSE stream, dispatch events to TanStack Query, reconnect with backoff.
 *
 * Reads token + base URL on each connect attempt rather than at hook-mount so a Tauri
 * shell that hot-swaps the loopback token (e.g. workspace switch) is handled correctly
 * after the next reconnect.
 *
 * Cancellation: the returned cleanup function aborts the in-flight fetch + cancels any
 * pending reconnect timer. React StrictMode's double-effect-invoke is handled gracefully —
 * each effect run gets its own AbortController.
 */
export function useChangeEventStream(): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const abort = new AbortController();
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let backoffMs = 1000; // start at 1s; cap at 30s
    const MAX_BACKOFF = 30_000;

    const connect = async (): Promise<void> => {
      const base = getBaseUrl();
      const token = getToken();
      // 2026-05-31 fix: previously bailed out entirely when no token was present, which
      // meant the SSE stream NEVER connected on either web (Clerk auth via cookies, no
      // client-side bearer) or desktop (loopback token lives in `__ALETHEIA_CHAT__`, not
      // the `__ALETHEIA__` global getToken() reads). Result: MCP writes never reached the
      // UI — task #92 looked complete but the stream never actually ran.
      //
      // Mirror `api/client.ts`'s pattern: send the bearer if we have one, otherwise rely
      // on the SWA proxy (proxy.js) to inject `Authorization: Bearer <ALETHEIA_BACKEND_TOKEN>`
      // server-side using the SWA platform's x-ms-client-principal headers. Same-origin
      // requests route through the proxy automatically.
      const url = `${base}/api/v1/events`;
      const headers: Record<string, string> = { Accept: 'text/event-stream' };
      if (token) headers.Authorization = `Bearer ${token}`;
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers,
          signal: abort.signal,
          // Don't send cookies — this matches the rest of the client's `credentials: 'omit'`.
          credentials: 'omit',
        });
        if (!res.ok) {
          // 4xx / 5xx — abandon this attempt and back off. 401 likely means the token is
          // stale; we'll retry, and by the time the user notices the workspace switch the
          // backoff will be short enough that they don't see the lag.
          // eslint-disable-next-line no-console
          console.warn('[events] SSE handshake failed', res.status);
          scheduleReconnect();
          return;
        }
        if (res.body === null) {
          scheduleReconnect();
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        backoffMs = 1000; // successful handshake → reset backoff
        // Loop until the stream closes (server end, network blip, or abort).
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // SSE messages are delimited by a blank line. Split on `\n\n` and keep the trailing
          // partial in the buffer for the next read.
          const messages = buffer.split('\n\n');
          buffer = messages.pop() ?? '';
          for (const msg of messages) {
            handleMessage(msg);
          }
        }
        // Reader closed cleanly — server may have rolled. Reconnect.
        scheduleReconnect();
      } catch (err) {
        if (abort.signal.aborted) return; // unmount; bail without rescheduling.
        // eslint-disable-next-line no-console
        console.warn('[events] SSE connection error', err);
        scheduleReconnect();
      }
    };

    const handleMessage = (msg: string): void => {
      // SSE message lines: `data: ...`, `event: ...`, `id: ...`, or `:` comments. We only
      // care about `data:` lines that parse as our ChangeEvent shape.
      const dataLines = msg
        .split('\n')
        .filter((l) => l.startsWith('data:'))
        .map((l) => l.slice(5).trim());
      if (dataLines.length === 0) return;
      const raw = dataLines.join('\n');
      let parsed: ChangeEvent;
      try {
        parsed = JSON.parse(raw) as ChangeEvent;
      } catch {
        return; // ignore malformed payloads
      }
      if (!Array.isArray(parsed.families)) return;
      for (const family of parsed.families) {
        const keys = FAMILY_TO_KEYS[family];
        if (!keys) continue;
        for (const key of keys) {
          void queryClient.invalidateQueries({ queryKey: [...key] });
        }
      }
    };

    const scheduleReconnect = (): void => {
      if (abort.signal.aborted) return;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        void connect();
      }, backoffMs);
      backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF);
    };

    void connect();

    return () => {
      abort.abort();
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
    };
    // Intentionally empty dep array — we open the stream once on mount, reconnect logic
    // is internal. queryClient identity is stable across renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
